import { useGetActivityMetricsQuery } from 'api/recoveryAi'
import useRecoveryAiOn from 'hooks/useRecoveryAiOn'
import { buildTranslate } from 'locales'
import { ESCALATIONS_PATH } from 'scenes/RecoveryAI/routes'

const t = buildTranslate('home')

export interface NeedsAttentionRowData {
  id: 'escalations'
  count: number
  srLabel: string // already-translated, from the REAL count (not the capped 99+)
  title: string
  titleSuffix?: string // trailing copy; self-margined so it reads with or without a pill
  pilotLabel?: string // set -> row renders the static gray PILOT pill
  buttonLabel: string
  to: string // React Router path for the row's action
}

export interface UseNeedsAttentionResult {
  showSection: boolean
  rows: NeedsAttentionRowData[]
}

const useNeedsAttention = (): UseNeedsAttentionResult => {
  // "RAI flag on + config active + this viewer can reach /recovery_ai" — shared
  // with the Home feedback tiles (ButtonRow / RbacButtonRow), which branch their
  // destination on the same condition, so the section's gate and the tiles' can't
  // diverge from each other or from the page the CTA links into.
  const raiOn = useRecoveryAiOn()

  // Count only fetched when the section can render (RAI on).
  const { data: metrics } = useGetActivityMetricsQuery(undefined, {
    skip: !raiOn,
  })
  // Row 1 (RecoveryAI escalations) self-gates on an active, accessible RAI:
  // zero the count when !raiOn, guarding against a stale non-zero `metrics`
  // value if raiOn flips false after the query already resolved (a skipped
  // RTK Query hook keeps returning its last successful result, not undefined).
  const row1Count = raiOn ? metrics?.actionRequiredCount ?? 0 : 0

  const candidateRows: NeedsAttentionRowData[] = [
    {
      id: 'escalations',
      count: row1Count,
      srLabel: t('needs_attention.count_label', { count: row1Count }),
      title: t('needs_attention.row1_product'),
      titleSuffix: t('needs_attention.row1_suffix'),
      buttonLabel: t('needs_attention.review_escalations'),
      to: ESCALATIONS_PATH,
    },
  ]

  const rows = candidateRows.filter(row => row.count > 0) // hide-row-at-0
  // Only the RAI escalations row exists today, so in practice the section shows
  // only when RAI is active/accessible AND the count is non-zero. Gate is
  // `rows.length > 0` (not `&& raiOn`) so a future non-RAI row can surface it
  // without reworking this — the AI-563 review kept the array shape for that.
  const showSection = rows.length > 0

  return { showSection, rows }
}

export default useNeedsAttention
